import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { formatINR } from '@/utils/format';
import AddComponentModal from './components/AddComponentModal';
import BOMExplosionModal from './components/BOMExplosionModal';
import BOMPrintModal from './components/BOMPrintModal';
import axios from 'axios';
import { useWarehouseStore } from '@/stores/warehouseStore';
import {
  getItemsWithVariantsForBOM,
  type ItemResponse,
  type BOMDropdownGroup,
  BOMDropdownVariant,
} from '../../../api/item.api';

// ============================================
// TYPES
// ============================================
type BomStatus = 'DRAFT' | 'APPROVED' | 'ACTIVE' | 'OBSOLETE';

const STATUS_STYLE: Record<BomStatus, { label: string; cls: string; icon: string }> = {
  DRAFT: { label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: 'ri-draft-line' },
  APPROVED: { label: 'Approved', cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: 'ri-shield-check-line' },
  ACTIVE: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-checkbox-circle-line' },
  OBSOLETE: { label: 'Obsolete', cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: 'ri-forbid-line' },
};
interface FormBOMItem {
  id: string;
  parentId: string | null;
  itemId: string;
  itemName: string;
  itemCode: string;
  itemType: string;
  // Quantity and unit as entered (250 g); `toStock` turns it into the item's stock unit (× 0.001 → Kg)
  qtyPerUnit: number;
  unit: string;
  unitId?: string | null;
  stockUnit?: string;
  toStock?: number;
  scrapPct: number;
  standardCost: number;
  level: number;
  hasSubBOM: boolean;
  subBOMId: string | null;
  qcRequired: boolean;
  notes: string | null;
  rolledUpCost?: number;
}

interface UnitOption {
  id: string;
  name: string;
  shortName: string;
  category: string;
  factor: number;
  isActive: boolean;
}

// ============================================
// API SERVICE
// ============================================
const API_BASE = `${import.meta.env.VITE_API_URL}/api`;
const BOM_API_BASE = `${API_BASE}/v1/manufacturing/bom`;

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  },
});

const bomAPI = {
  create: async (data: any) => {
    const response = await axios.post(BOM_API_BASE, data, getAuthHeaders());
    return response.data;
  },
  getById: async (id: string) => {
    const response = await axios.get(`${BOM_API_BASE}/${id}`, getAuthHeaders());
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await axios.put(
      `${BOM_API_BASE}/${id}`,
      data,
      getAuthHeaders(),
    );
    return response.data;
  },
  duplicate: async (id: string) => {
    const response = await axios.post(
      `${BOM_API_BASE}/${id}/duplicate`,
      {},
      getAuthHeaders(),
    );
    return response.data;
  },
  linkToItemMaster: async (id: string) => {
    const response = await axios.post(
      `${BOM_API_BASE}/${id}/link`,
      {},
      getAuthHeaders(),
    );
    return response.data;
  },
  getUnits: async (): Promise<UnitOption[]> => {
    const response = await axios.get(`${API_BASE}/v1/unit/all`, getAuthHeaders());
    return response.data?.data ?? [];
  },
  /** approve | send_back | activate | obsolete */
  changeStatus: async (id: string, action: string, note?: string) => {
    const response = await axios.post(`${BOM_API_BASE}/${id}/status`, { action, note }, getAuthHeaders());
    return response.data;
  },
  canApprove: async (): Promise<boolean> => {
    const response = await axios.get(`${BOM_API_BASE}/approval/me`, getAuthHeaders());
    return Boolean(response.data?.data?.canApprove);
  },
  getNextCode: async () => {
    const response = await axios.get(
      `${BOM_API_BASE}/next-code`,
      getAuthHeaders(),
    );
    return response.data;
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;
const fmtQty = (n: number) => String(Number(n.toFixed(6)));

/** Quantity of a component in its item's stock unit. */
const stockQtyOf = (i: FormBOMItem) => i.qtyPerUnit * (i.toStock ?? 1);
function getDescendantIds(items: FormBOMItem[], parentId: string): string[] {
  const result: string[] = [];
  const direct = items.filter((i) => i.parentId === parentId);
  direct.forEach((child) => {
    result.push(child.id);
    result.push(...getDescendantIds(items, child.id));
  });
  return result;
}

const typeBadgeClass = (type: string) => {
  const map: Record<string, string> = {
    RAW_MATERIAL: 'bg-amber-100 text-amber-700 border-amber-200',
    SEMI_FINISHED: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    CONSUMABLE: 'bg-sky-100 text-sky-700 border-sky-200',
    PACKAGING: 'bg-slate-100 text-slate-600 border-slate-200',
    FINISHED_GOOD: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return map[type] || 'bg-gray-100 text-gray-600 border-gray-200';
};

const typeLabel = (type: string) => {
  const map: Record<string, string> = {
    RAW_MATERIAL: 'Raw',
    SEMI_FINISHED: 'Semi',
    CONSUMABLE: 'Consumable',
    PACKAGING: 'Packaging',
    FINISHED_GOOD: 'Finished',
  };
  return map[type] || type;
};

const formatItemTypeNice = (rawType?: string) => {
  const t = String(rawType || 'FINISHED_GOOD').toUpperCase();
  if (t === 'RAW_MATERIAL') return 'Raw Material';
  if (t === 'SEMI_FINISHED') return 'Semi-Finished';
  if (t === 'FINISHED_GOOD') return 'Finished Good';
  if (t === 'CONSUMABLE') return 'Consumable';
  if (t === 'PACKAGING') return 'Packaging';
  if (t === 'SERVICE') return 'Service';
  return t;
};

const formatItemTypeColor = (rawType?: string) => {
  const t = String(rawType || 'FINISHED_GOOD').toUpperCase();
  if (t === 'RAW_MATERIAL') return 'bg-orange-50 text-orange-700 border-orange-200';
  if (t === 'SEMI_FINISHED') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (t === 'FINISHED_GOOD') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (t === 'CONSUMABLE') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (t === 'PACKAGING') return 'bg-slate-50 text-slate-700 border-slate-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
};

// ============================================
// MAIN COMPONENT
// ============================================
export default function BOMForm() {
  const { id: editId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const isEditing = !!editId;

  // ✅ Global warehouse store
  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();

  // ✅ Dynamic data states
  const [itemsList, setItemsList] = useState<BOMDropdownGroup[]>([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [linkToItemMaster, setLinkToItemMaster] = useState(false);
  const [showLinkConfirm, setShowLinkConfirm] = useState(false);

  // Form state
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [selectedProduct, setSelectedProduct] = useState<ItemResponse | null>(
    null,
  );
  const [bomCode, setBomCode] = useState('');
  const [version, setVersion] = useState('1.0');
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().split('T')[0],
  );
  // Draft → Approved → Active (Obsolete when retired); changed only through the approval actions
  const [status, setStatus] = useState<BomStatus>('DRAFT');
  // "This BOM is for ___ units": component quantities are entered for this many units
  const [baseQty, setBaseQty] = useState('1');
  const [approval, setApproval] = useState<{ approvedBy: string | null; approvedAt: string | null; reviewNote: string | null; createdBy: string | null }>({
    approvedBy: null, approvedAt: null, reviewNote: null, createdBy: null,
  });
  const [canApprove, setCanApprove] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState('');
  // Active and obsolete BOMs are what production used: read-only, changed through a new version
  const isLocked = status === 'ACTIVE' || status === 'OBSOLETE';
  const baseQtyNum = Number(baseQty) > 0 ? Number(baseQty) : 1;

  useEffect(() => {
    bomAPI.canApprove().then(setCanApprove).catch(() => setCanApprove(false));
  }, []);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<FormBOMItem[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [rolledUpCosts, setRolledUpCosts] = useState<Record<string, number>>();
  const [rolledUpTotal, setRolledUpTotal] = useState(0);

  // UI state
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalParentId, setAddModalParentId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCostConfirm, setShowCostConfirm] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [showExplodeModal, setShowExplodeModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showLevelRules, setShowLevelRules] = useState(true);


  // ============================================
  // FETCH ITEMS + VARIANTS FOR BOM DROPDOWN
  // ============================================
  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoadingItems(true);
        const data = await getItemsWithVariantsForBOM(selectedWarehouseId || undefined);
        if (data) setItemsList(data);
      } catch (error) {
        console.error('Failed to fetch items:', error);
        toast.error('Failed to load items');
      } finally {
        setLoadingItems(false);
      }
    };
    fetchItems();
  }, [toast, selectedWarehouseId]);

  // ============================================
  // FETCH NEXT SEQUENTIAL BOM CODE (if creating new)
  // ============================================
  useEffect(() => {
    if (!isEditing) {
      const fetchNextCode = async () => {
        try {
          const res = await bomAPI.getNextCode();
          if (res?.success && res?.data?.code) {
            setBomCode(res.data.code);
          }
        } catch (error) {
          console.error('Failed to load next BOM code:', error);
        }
      };
      fetchNextCode();
    }
  }, [isEditing]);

  // ============================================
  // FETCH EXISTING BOM (if editing)
  // ============================================
  useEffect(() => {
    if (isEditing && editId) {
      const loadBom = async () => {
        try {
          setInitialLoading(true);
          const response = await bomAPI.getById(editId);
          if (response.success && response.data) {
            const bom = response.data;

            // ✅ Set product details
            setSelectedProductId(bom.product_id);
            const product = itemsList.find((i) => i.id === bom.product_id);
            setSelectedProduct((product as any) || null);
            setBomCode(bom.code);
            setVersion(bom.version);
            setLinkToItemMaster(bom.link_to_item_master || false);
            setEffectiveFrom(
              new Date(bom.effective_from).toISOString().split('T')[0],
            );
            setStatus((String(bom.status || 'DRAFT').toUpperCase() as BomStatus) || 'DRAFT');
            setNotes(bom.notes || '');
            const loadedBase = Number(bom.base_qty) > 0 ? Number(bom.base_qty) : 1;
            setBaseQty(String(loadedBase));
            setApproval({
              approvedBy: bom.approved_by_name || null,
              approvedAt: bom.approved_at || null,
              reviewNote: bom.review_note || null,
              createdBy: bom.created_by_name || null,
            });

            // ✅ CREATE ROOT ITEM (product itself)
            const rootId = crypto.randomUUID();
            const rootItem: FormBOMItem = {
              id: rootId,
              parentId: null,
              itemId: bom.product_id,
              itemName: bom.product_name,
              itemCode: bom.product_code || '',
              itemType: 'FINISHED_GOOD',
              qtyPerUnit: 1,
              unit: product?.unit_name || 'Pcs',
              scrapPct: 0,
              standardCost: parseFloat(String(bom.total_material_cost || 0)),
              level: 0,
              hasSubBOM: false,
              subBOMId: null,
              qcRequired: false,
              notes: null,
            };

            // ✅ FORMAT COMPONENTS (level > 0)
            let formattedComponents: FormBOMItem[] = [];
            if (bom.items && bom.items.length > 0) {
              formattedComponents = bom.items.map((item: any) => ({
                id: item.id || crypto.randomUUID(),
                parentId: item.parent_id || rootId,
                itemId: item.item_id,
                itemName: item.item_name,
                itemCode: item.item_code || '',
                itemType: item.item_type || 'RAW_MATERIAL',
                ...(() => {
                  const stockUnit = item.unit_of_measure || item.unit_name || 'Pcs';
                  // Stored per 1 unit of the product; shown for the BOM's base quantity
                  const bomBase = Number(bom.base_qty) > 0 ? Number(bom.base_qty) : 1;
                  const stockQtyForBase = round6((parseFloat(item.quantity) || 0) * bomBase) || 1;
                  const enteredQty = parseFloat(item.entered_qty);
                  // Entered in another unit: show that, and keep the ratio to the stock unit
                  if (item.entered_unit_id && enteredQty > 0) {
                    return {
                      qtyPerUnit: enteredQty,
                      unit: item.entered_unit_name || stockUnit,
                      unitId: item.entered_unit_id,
                      stockUnit,
                      toStock: stockQtyForBase / enteredQty,
                    };
                  }
                  return { qtyPerUnit: stockQtyForBase, unit: stockUnit, unitId: null, stockUnit, toStock: 1 };
                })(),
                scrapPct: parseFloat(item.scrap_percentage) || 0,
                standardCost: parseFloat(item.unit_price) || 0,
                level: item.level || 1,
                hasSubBOM: item.has_sub_bom || false,
                subBOMId: item.sub_bom_id || null,
                qcRequired: item.qc_required || false,
                notes: item.notes || null,
              }));
            }

            // ✅ SET ITEMS = ROOT + COMPONENTS
            setItems([rootItem, ...formattedComponents]);
            setExpandedRows(
              new Set([rootId, ...formattedComponents.map((i) => i.id)]),
            );
          }
        } catch (error) {
          console.error('Failed to load BOM:', error);
          toast.error('Failed to load BOM data');
        } finally {
          setInitialLoading(false);
        }
      };
      loadBom();
    }
  }, [isEditing, editId, itemsList, toast]);

  // ============================================
  // DERIVED VALUES
  // ============================================
  const displayItems = useMemo(
    () => items.filter((item) => item.level > 0),
    [items],
  );

  const maxLevel = useMemo(
    () => (items.length ? Math.max(...items.map((i) => i.level)) : 0),
    [items],
  );

  const costSummary = useMemo(() => {
    const components = items.filter((i) => i.level > 0);
    let raw = 0,
      semi = 0,
      consumable = 0,
      packaging = 0,
      scrapCost = 0;

    components.forEach((i) => {
      const baseCost = stockQtyOf(i) * i.standardCost;
      const scrapAmount = baseCost * (i.scrapPct / 100);
      const totalWithScrap = baseCost + scrapAmount;
      scrapCost += scrapAmount;

      if (i.itemType === 'RAW_MATERIAL') raw += totalWithScrap;
      else if (i.itemType === 'SEMI_FINISHED') semi += totalWithScrap;
      else if (i.itemType === 'CONSUMABLE') consumable += totalWithScrap;
      else if (i.itemType === 'PACKAGING') packaging += totalWithScrap;
    });

    const baseTotal = raw + semi + consumable + packaging;
    const total = rolledUpTotal > 0 ? rolledUpTotal : baseTotal;
    return { raw, semi, consumable, packaging, total, scrapCost };
  }, [items, rolledUpTotal]);

  const currentBOMObject = useMemo(() => {
    const cost = costSummary.total > 0 ? costSummary.total : rolledUpTotal;
    return {
      id: editId || 'new-bom',
      productId: selectedProductId || '',
      productName: selectedProduct?.name || '',
      productCode: selectedProduct?.code || '',
      code: bomCode,
      version: version,
      status: status,
      baseQty: baseQtyNum,
      effectiveFrom: effectiveFrom,
      notes: notes,
      totalMaterialCost: cost,
      total_material_cost: cost,
      isVariantBOM: false,
      variantName: '',
    };
  }, [editId, selectedProductId, selectedProduct, bomCode, version, status, baseQtyNum, effectiveFrom, notes, costSummary.total, rolledUpTotal]);

  const productDropdownData = useMemo(() => {
    const q = productSearchQuery.toLowerCase();
    // Rule: BOM Product must be a FINISHED_GOOD or SEMI_FINISHED item
    let filtered = itemsList.filter((group) => {
      const type = (group as any).item_type || group.category;
      return type === 'FINISHED_GOOD' || type === 'SEMI_FINISHED';
    });

    if (q) {
      filtered = filtered.filter((group) => {
        const matchesItem =
          group.name.toLowerCase().includes(q) ||
          (group.code && group.code.toLowerCase().includes(q));
        const matchesVariant = group.variants.some(
          (v) =>
            v.name.toLowerCase().includes(q) ||
            (v.code && v.code.toLowerCase().includes(q)),
        );
        return matchesItem || matchesVariant;
      });
    }
    return filtered;
  }, [itemsList, productSearchQuery]);

  const finishedProducts = useMemo(() => {
    return productDropdownData.filter((group) => {
      const rawType = String((group as any).item_type || group.category || '').toUpperCase().replace(/[\s-]/g, '_');
      return rawType.includes('FINISH') && !rawType.includes('SEMI');
    });
  }, [productDropdownData]);

  const semiFinishedProducts = useMemo(() => {
    return productDropdownData.filter((group) => {
      const rawType = String((group as any).item_type || group.category || '').toUpperCase().replace(/[\s-]/g, '_');
      return rawType.includes('SEMI');
    });
  }, [productDropdownData]);

  const otherProducts = useMemo(() => {
    return productDropdownData.filter((group) => {
      const rawType = String((group as any).item_type || group.category || '').toUpperCase().replace(/[\s-]/g, '_');
      const isFG = rawType.includes('FINISH') && !rawType.includes('SEMI');
      const isSF = rawType.includes('SEMI');
      return !isFG && !isSF;
    });
  }, [productDropdownData]);

  const availableComponents = useMemo(() => {
    const parent = addModalParentId ? items.find((i) => i.id === addModalParentId) : null;
    const isLevel2OrMore = parent && parent.level >= 1;

    const allComponents: BOMDropdownGroup[] = [];
    itemsList.forEach((group) => {
      // Types come as FINISHED_GOOD or "Finished Good"
      const type = String((group as any).item_type || group.category || '').toUpperCase().replace(/[\s-]/g, '_');
      // Rule: Finished Goods cannot be used as components
      if (type === 'FINISHED_GOOD') return;
      // Rule: Semi-Finished items cannot be added at Level 2+ (to avoid circular sub-assembly loops)
      if (isLevel2OrMore && type === 'SEMI_FINISHED') return;
      allComponents.push(group);
    });
    return allComponents;
  }, [itemsList, addModalParentId, items]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleSelectProduct = useCallback(
    (productId: string, selectedItem?: BOMDropdownGroup | BOMDropdownVariant) => {
      let product = selectedItem || itemsList.find((m) => m.id === productId);
      if (!product) return;

      setSelectedProductId(productId);

      let productName = product.name;
      let productCode = product.code;
      let productCategory = 'FINISHED_GOOD';
      let productPurchaseRate = product.purchase_rate;
      let productUnitName = product.unit_name;

      if ('variant_name' in product && product.variant_name) {
        productName = `${product.parent_item_name} - ${product.variant_name}`;
        productCategory = (product as any).item_type || 'FINISHED_GOOD';
      } else {
        productCategory = (product as any).item_type  || 'FINISHED_GOOD';
      }

      setSelectedProduct({
        id: product.id,
        name: productName,
        code: productCode,
        category: productCategory,
        purchase_rate: productPurchaseRate,
        unit_name: productUnitName,
      } as ItemResponse);

      setShowProductSearch(false);
      setProductSearchQuery('');

      const rootId = crypto.randomUUID();
      const rootItem: FormBOMItem = {
        id: rootId,
        parentId: null,
        itemId: product.id,
        itemName: productName,
        itemCode: productCode || '',
        itemType: productCategory,
        qtyPerUnit: 1,
        unit: productUnitName || 'Pcs',
        scrapPct: 0,
        standardCost: parseFloat(String(productPurchaseRate || 0)),
        level: 0,
        hasSubBOM: false,
        subBOMId: null,
        qcRequired: false,
        notes: null,
      };
      setItems([rootItem]);
      setExpandedRows(new Set([rootId]));

      // Sequential BOM code is kept automatically (BOM-001, BOM-002, etc.)
      if (!bomCode) {
        bomAPI.getNextCode().then((res) => {
          if (res?.success && res?.data?.code) {
            setBomCode(res.data.code);
          }
        }).catch(() => {});
      }
      setVersion('1.0');
    },
    [itemsList, bomCode],
  );

  const updateItemField = useCallback(
    (id: string, patch: Partial<FormBOMItem>) => {
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
      );
    },
    [],
  );

  // ── Component units: any unit of the same category as the item's stock unit ──
  const [allUnits, setAllUnits] = useState<UnitOption[]>([]);
  useEffect(() => {
    bomAPI.getUnits().then(setAllUnits).catch(() => setAllUnits([]));
  }, []);
  const unitByCode = useMemo(() => {
    const m = new Map<string, UnitOption>();
    allUnits.forEach((u) => m.set(u.shortName.toLowerCase(), u));
    return m;
  }, [allUnits]);

  const unitOptionsFor = useCallback(
    (item: FormBOMItem): UnitOption[] => {
      const stock = unitByCode.get(String(item.stockUnit || item.unit).toLowerCase());
      if (!stock || stock.category === 'OTHER') return stock ? [stock] : [];
      return allUnits.filter(
        (u) => u.category === stock.category && (u.isActive || u.shortName === item.unit),
      );
    },
    [allUnits, unitByCode],
  );

  // Switching the unit keeps the same physical quantity: 0.25 Kg → 250 g
  const changeItemUnit = useCallback(
    (item: FormBOMItem, unitId: string) => {
      const next = allUnits.find((u) => u.id === unitId);
      const stock = unitByCode.get(String(item.stockUnit || item.unit).toLowerCase());
      if (!next || !stock) return;
      const toStock = next.factor / stock.factor;
      const isStock = next.id === stock.id;
      updateItemField(item.id, {
        unit: next.shortName,
        unitId: isStock ? null : next.id,
        stockUnit: stock.shortName,
        toStock: isStock ? 1 : toStock,
        qtyPerUnit: round6(stockQtyOf(item) / (isStock ? 1 : toStock)),
      });
    },
    [allUnits, unitByCode, updateItemField],
  );

  const handleDeleteItem = useCallback((id: string) => {
    setItems((prev) => {
      const toDelete = new Set([id, ...getDescendantIds(prev, id)]);
      return prev.filter((it) => !toDelete.has(it.id));
    });
  }, []);

  const handleAddComponent = useCallback(
    (selectedItem: BOMDropdownGroup | BOMDropdownVariant) => {
      const parentId = addModalParentId;
      const parent = items.find((i) => i.id === parentId);
      const level = parent ? parent.level + 1 : 0;

      let itemType = 'RAW_MATERIAL';
      let itemName = selectedItem.name;

      if ('variant_name' in selectedItem && selectedItem.variant_name) {
        itemType = (selectedItem as any).item_type || 'RAW_MATERIAL';
        itemName = `${selectedItem.parent_item_name} - ${selectedItem.variant_name}`;
      } else {
        itemType = (selectedItem as any).item_type  || 'RAW_MATERIAL';
      }

      const newItem: FormBOMItem = {
        id: crypto.randomUUID(),
        parentId: parentId || null,
        itemId: selectedItem.id,
        itemName: itemName,
        itemCode: selectedItem.code || '',
        itemType: itemType,
        qtyPerUnit: 1,
        unit: selectedItem.unit_name || 'Pcs',
        unitId: null,
        stockUnit: selectedItem.unit_name || 'Pcs',
        toStock: 1,
        scrapPct: 0,
        standardCost: parseFloat(String(selectedItem.purchase_rate || 0)),
        level,
        hasSubBOM: false,
        subBOMId: null,
        qcRequired: false,
        notes: null,
      };
      setItems((prev) => [...prev, newItem]);
      if (parentId) setExpandedRows((prev) => new Set([...prev, parentId]));
      setShowAddModal(false);
      setAddModalParentId(null);
    },
    [addModalParentId, items],
  );

  const openAddModal = useCallback((parentId: string | null = null) => {
    setAddModalParentId(parentId);
    setShowAddModal(true);
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCostRollup = useCallback(() => {
    const childrenMap = new Map<string, string[]>();
    items.forEach((it) => {
      if (it.parentId) {
        if (!childrenMap.has(it.parentId)) childrenMap.set(it.parentId, []);
        childrenMap.get(it.parentId)!.push(it.id);
      }
    });

    const costs = new Map<string, number>();
    function computeCost(itemId: string): number {
      if (costs.has(itemId)) return costs.get(itemId)!;
      const item = items.find((i) => i.id === itemId);
      if (!item) return 0;
      const children = childrenMap.get(itemId) || [];
      if (children.length === 0) {
        const cost =
          stockQtyOf(item) * (1 + item.scrapPct / 100) * item.standardCost;
        costs.set(itemId, cost);
        return cost;
      }
      const cost = children.reduce((sum, cid) => sum + computeCost(cid), 0);
      costs.set(itemId, cost);
      return cost;
    }

    items.forEach((it) => computeCost(it.id));
    const newMap: Record<string, number> = {};
    costs.forEach((cost, id) => {
      newMap[id] = cost;
    });
    setRolledUpCosts(newMap);
    const newTotal = items
      .filter((i) => i.level > 0)
      .reduce((sum, it) => sum + (costs.get(it.id) || 0), 0);
    setRolledUpTotal(newTotal);
    toast.success(`Cost rollup complete: ${formatINR(newTotal)}`);
    setShowCostConfirm(true);
  }, [items, toast]);

  const handleSave = useCallback(async () => {
    if (!selectedProductId) {
      toast.error('Please select a product');
      return;
    }
    if (isLocked) {
      toast.error('An active or obsolete BOM cannot be changed — duplicate it as a new version');
      return;
    }
    if (!(Number(baseQty) > 0)) {
      toast.error('BOM base quantity must be greater than 0');
      return;
    }
    if (items.filter((i) => i.level > 0).length === 0) {
      toast.error('Add at least one component');
      return;
    }

    setSaving(true);
    try {
      const apiItems = items
        .filter((it) => it.level > 0)
        .map((it, index) => ({
          itemId: it.itemId,
          quantity: round6(stockQtyOf(it)),
          // The server recomputes `quantity` from these when the unit differs from the stock unit
          enteredQty: it.unitId ? it.qtyPerUnit : null,
          enteredUnitId: it.unitId || null,
          scrapPercentage: it.scrapPct,
          sequenceNo: index + 1,
        }));

      // Company-level BOM: no warehouse. Quantities are for `baseQty` units; status follows approval.
      const bomData = {
        code: bomCode,
        productId: selectedProductId,
        version: version,
        baseQty: Number(baseQty),
        effectiveFrom: effectiveFrom,
        effectiveTo: null,
        isVariantBom: false,
        variantName: undefined,
        linkToItemMaster: linkToItemMaster,
        items: apiItems,
      };

      let response;
      if (isEditing && editId) {
        response = await bomAPI.update(editId, {
          version,
          baseQty: Number(baseQty),
          effectiveFrom,
          effectiveTo: null,
          items: apiItems,
          linkToItemMaster: linkToItemMaster,
        });
      } else {
        response = await bomAPI.create(bomData);
      }

      if (response.success) {
        toast.success(response.message || (isEditing ? 'BOM updated successfully' : 'BOM saved as draft'));
        navigate('/manufacturing/bom-list');
      } else {
        toast.error(response.message || 'Failed to save BOM');
      }
    } catch (error: any) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.message || 'Failed to save BOM');
    } finally {
      setSaving(false);
    }
  }, [
    selectedProductId,
    items,
    isEditing,
    editId,
    bomCode,
    version,
    baseQty,
    isLocked,
    effectiveFrom,
    linkToItemMaster,
    toast,
    navigate,
  ]);

  // Approval actions: approve, send back (with a note), activate, retire
  const runStatusAction = useCallback(
    async (action: 'approve' | 'send_back' | 'activate' | 'obsolete', note?: string) => {
      if (!editId) return;
      setStatusBusy(true);
      try {
        const res = await bomAPI.changeStatus(editId, action, note);
        if (res.success) {
          setStatus(res.data.status as BomStatus);
          if (action === 'approve') setApproval((a) => ({ ...a, approvedBy: 'you', approvedAt: new Date().toISOString(), reviewNote: null }));
          if (action === 'send_back') setApproval((a) => ({ ...a, approvedBy: null, approvedAt: null, reviewNote: note || null }));
          toast.success(res.message || 'Status updated');
        } else {
          toast.error(res.message || 'Could not change status');
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Could not change status');
      } finally {
        setStatusBusy(false);
        setSendBackOpen(false);
        setSendBackNote('');
      }
    },
    [editId, toast],
  );

  // ============================================
  // RENDER
  // ============================================

  if (initialLoading || loadingItems) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-indigo-600 animate-spin" />
            <p className="mt-2 text-slate-500">Loading...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b] flex items-center gap-2">
              {isEditing ? (isLocked ? 'View BOM' : 'Edit BOM') : 'Create BOM'}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_STYLE[status].cls}`}>
                <i className={STATUS_STYLE[status].icon} /> {STATUS_STYLE[status].label}
              </span>
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {selectedProduct
                ? `for ${selectedProduct.name}`
                : 'Define a new Bill of Materials'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              <i className="ri-building-line mr-1" /> Applies to all warehouses
              {selectedWarehouseName ? ` · stock checked in ${selectedWarehouseName}` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            {isEditing && (
              <>
                <button
                  onClick={() => setShowExplodeModal(true)}
                  className="h-9 px-3 rounded-lg border border-emerald-200 text-sm font-medium text-emerald-700 hover:bg-emerald-50 cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-node-tree" /> Explode
                </button>
                <button
                  onClick={() => setShowDuplicateConfirm(true)}
                  className="h-9 px-3 rounded-lg border border-amber-200 text-sm font-medium text-amber-700 hover:bg-amber-50 cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-file-copy-line" /> Duplicate
                </button>
              </>
            )}
            <button
              onClick={() => setShowPrintModal(true)}
              className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-printer-line" /> Print
            </button>
            <button
              onClick={() => navigate('/manufacturing/bom')}
              className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || isLocked}
              title={isLocked ? 'Active / obsolete BOMs are read-only — duplicate to make a new version' : undefined}
              className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-[#4338ca] disabled:opacity-60 cursor-pointer flex items-center gap-2"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-line" /> Save BOM
                </>
              )}
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 mb-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Product Selection */}
              <div className="md:col-span-2 relative">
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Product <span className="text-red-500">*</span>
                </label>
                <div
                  className={`w-full min-h-[40px] px-3 py-2 text-sm border rounded-lg cursor-pointer flex items-center justify-between ${selectedProduct ? 'bg-white border-[#e2e8f0]' : 'bg-slate-50 border-[#e2e8f0] text-slate-400'}`}
                  onClick={() => !isEditing && setShowProductSearch(true)}
                >
                  <span>
                    {selectedProduct
                      ? selectedProduct.name
                      : 'Search and select a product...'}
                  </span>
                  {!isEditing && (
                    <i className="ri-search-line text-slate-400" />
                  )}
                </div>
                {showProductSearch && !isEditing && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg max-h-80 overflow-y-auto">
                    <div className="p-2 border-b border-[#e2e8f0]">
                      <input
                        autoFocus
                        type="text"
                        value={productSearchQuery}
                        onChange={(e) => setProductSearchQuery(e.target.value)}
                        placeholder="Search products..."
                        className="w-full h-9 px-3 text-sm bg-slate-50 border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                      />
                    </div>
                    <div className="py-1">
                      {/* Helper for rendering group */}
                      {(() => {
                        const renderGroup = (group: BOMDropdownGroup) => (
                          <div key={group.id} className="border-b border-slate-50 last:border-b-0">
                            <button
                              type="button"
                              onClick={() => handleSelectProduct(group.id, group)}
                              className="w-full px-3 py-2 text-left text-sm font-semibold text-[#1e293b] hover:bg-indigo-50/80 cursor-pointer flex items-center justify-between gap-2 transition-colors"
                            >
                              <span className="truncate">{group.name}</span>
                              <div className="flex items-center gap-2 shrink-0">
                                {/* <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${formatItemTypeColor((group as any).item_type || group.category)}`}>
                                  {formatItemTypeNice((group as any).item_type || group.category)}
                                </span> */}
                                <span className="text-xs text-slate-400 font-mono">
                                  {group.code}
                                </span>
                              </div>
                            </button>
                            {group.variants.length > 0 && (
                              <div className="ml-6 border-l border-slate-200 pl-2">
                                {group.variants.map((variant) => (
                                  <button
                                    key={variant.id}
                                    type="button"
                                    onClick={() =>
                                      handleSelectProduct(variant.id, variant)
                                    }
                                    className="w-full px-3 py-1.5 text-left text-sm text-[#64748b] hover:bg-indigo-50/80 cursor-pointer flex items-center justify-between gap-2 transition-colors"
                                  >
                                    <div className="flex items-center gap-1 truncate">
                                      <i className="ri-subtract-line text-xs text-slate-400" />
                                      <span className="truncate">{variant.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${formatItemTypeColor((variant as any).item_type || (group as any).item_type || group.category)}`}>
                                        {formatItemTypeNice((variant as any).item_type || (group as any).item_type || group.category)}
                                      </span>
                                      <span className="text-xs text-slate-400 font-mono">
                                        {variant.code}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );

                        return (
                          <>
                            {/* 1. Finished Goods Section */}
                            {finishedProducts.length > 0 && (
                              <div>
                                <div className="sticky top-0 z-10 px-3 py-1.5 bg-emerald-50/95 backdrop-blur-xs border-y border-emerald-200 flex items-center justify-between text-xs font-semibold text-emerald-800">
                                  <div className="flex items-center gap-1.5">
                                    <i className="ri-checkbox-circle-fill text-emerald-600 text-sm" />
                                    <span>Finished Goods ({finishedProducts.length})</span>
                                  </div>
                                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-200/80 px-1.5 py-0.5 rounded uppercase">
                                    Finished Good
                                  </span>
                                </div>
                                <div>{finishedProducts.map(renderGroup)}</div>
                              </div>
                            )}

                            {/* 2. Semi-Finished Goods Section */}
                            {semiFinishedProducts.length > 0 && (
                              <div>
                                <div className="sticky top-0 z-10 px-3 py-1.5 bg-amber-50/95 backdrop-blur-xs border-y border-amber-200 flex items-center justify-between text-xs font-semibold text-amber-800">
                                  <div className="flex items-center gap-1.5">
                                    <i className="ri-settings-4-fill text-amber-600 text-sm" />
                                    <span>Semi-Finished Goods ({semiFinishedProducts.length})</span>
                                  </div>
                                  <span className="text-[10px] font-bold text-amber-700 bg-amber-200/80 px-1.5 py-0.5 rounded uppercase">
                                    Semi Finished
                                  </span>
                                </div>
                                <div>{semiFinishedProducts.map(renderGroup)}</div>
                              </div>
                            )}

                            {/* 3. Other Goods Section */}
                            {otherProducts.length > 0 && (
                              <div>
                                <div className="sticky top-0 z-10 px-3 py-1.5 bg-slate-100/95 backdrop-blur-xs border-y border-slate-200 flex items-center justify-between text-xs font-semibold text-slate-700">
                                  <span>Other Goods ({otherProducts.length})</span>
                                </div>
                                <div>{otherProducts.map(renderGroup)}</div>
                              </div>
                            )}

                            {productDropdownData.length === 0 && (
                              <div className="px-3 py-4 text-center text-sm text-slate-400">
                                No products found
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* BOM Code */}
              <div>
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  BOM Code
                </label>
                <input
                  type="text"
                  value={bomCode}
                  onChange={(e) => setBomCode(e.target.value)}
                  className="w-full h-10 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                />
              </div>

              {/* Version */}
              <div>
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Version
                </label>
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="w-full h-10 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                />
              </div>

              {/* Effective From */}
              <div>
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Effective From <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  className="w-full h-10 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                />
              </div>

              {/* Base quantity: the BOM is for this many units of the product */}
              <div>
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  BOM Base Quantity <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 whitespace-nowrap">This BOM is for</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={baseQty}
                    disabled={isLocked}
                    onChange={(e) => setBaseQty(e.target.value)}
                    className="w-28 h-10 px-3 text-sm text-right bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] disabled:bg-slate-50"
                  />
                  <span className="text-sm text-slate-600 font-medium">{selectedProduct?.unit_name || 'unit(s)'}</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Enter component quantities for this many units (e.g. a 100 L batch). Default 1.</p>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] resize-none"
                  placeholder="Optional notes..."
                />
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-slate-50 border border-[#e2e8f0] rounded-xl p-4">
              <h3 className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-3">
                BOM Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Applies to</span>
                  <span className="text-sm font-medium text-[#1e293b]">All warehouses</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Components</span>
                  <span className="text-sm font-medium text-[#1e293b]">
                    {items.filter((i) => i.level > 0).length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Levels</span>
                  <span className="text-sm font-medium text-[#1e293b]">
                    {maxLevel + 1}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Cost for {baseQtyNum} {selectedProduct?.unit_name || 'unit(s)'}</span>
                  <span className="text-sm font-semibold text-[#1e293b]">
                    {formatINR(costSummary.total)}
                  </span>
                </div>
                {baseQtyNum !== 1 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Cost per {selectedProduct?.unit_name || 'unit'}</span>
                    <span className="text-sm font-medium text-[#1e293b]">{formatINR(costSummary.total / baseQtyNum)}</span>
                  </div>
                )}

                {/* Approval: Draft → Approved → Active */}
                <div className="pt-2 border-t border-[#e2e8f0] space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Approval</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_STYLE[status].cls}`}>
                      <i className={STATUS_STYLE[status].icon} /> {STATUS_STYLE[status].label}
                    </span>
                  </div>
                  {approval.approvedBy && status !== 'DRAFT' && (
                    <p className="text-[11px] text-slate-500">
                      Approved by {approval.approvedBy}
                      {approval.approvedAt ? ` · ${new Date(approval.approvedAt).toLocaleDateString('en-IN')}` : ''}
                    </p>
                  )}
                  {approval.reviewNote && status === 'DRAFT' && (
                    <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                      <i className="ri-chat-quote-line mr-1" /> Sent back: {approval.reviewNote}
                    </p>
                  )}
                  {!isEditing ? (
                    <p className="text-[11px] text-slate-400">Saved as a draft. A manager or admin approves it, then it can be activated.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {status === 'DRAFT' && canApprove && (
                        <button
                          disabled={statusBusy}
                          onClick={() => void runStatusAction('approve')}
                          className="h-7 px-2.5 rounded-md bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 disabled:opacity-60 cursor-pointer"
                        >
                          <i className="ri-shield-check-line mr-1" />Approve
                        </button>
                      )}
                      {status === 'APPROVED' && canApprove && (
                        <>
                          <button
                            disabled={statusBusy}
                            onClick={() => void runStatusAction('activate')}
                            className="h-7 px-2.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60 cursor-pointer"
                          >
                            <i className="ri-play-circle-line mr-1" />Activate
                          </button>
                          <button
                            disabled={statusBusy}
                            onClick={() => setSendBackOpen(true)}
                            className="h-7 px-2.5 rounded-md border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-50 disabled:opacity-60 cursor-pointer"
                          >
                            <i className="ri-arrow-go-back-line mr-1" />Send back
                          </button>
                        </>
                      )}
                      {status !== 'OBSOLETE' && (status !== 'ACTIVE' || canApprove) && (
                        <button
                          disabled={statusBusy}
                          onClick={() => void runStatusAction('obsolete')}
                          className="h-7 px-2.5 rounded-md border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-50 disabled:opacity-60 cursor-pointer"
                        >
                          <i className="ri-forbid-line mr-1" />Mark obsolete
                        </button>
                      )}
                      {!canApprove && (status === 'DRAFT' || status === 'APPROVED') && (
                        <p className="text-[11px] text-slate-400 w-full">
                          {status === 'DRAFT' ? 'Waiting for approval by a manager or admin.' : 'Approved — a manager or admin can activate it.'}
                        </p>
                      )}
                      {isLocked && (
                        <p className="text-[11px] text-slate-400 w-full">Read-only. Use Duplicate to make a new version.</p>
                      )}
                    </div>
                  )}
                  {sendBackOpen && (
                    <div className="space-y-1.5">
                      <textarea
                        value={sendBackNote}
                        onChange={(e) => setSendBackNote(e.target.value)}
                        rows={2}
                        autoFocus
                        placeholder="What should the author change?"
                        className="w-full px-2 py-1.5 text-xs bg-white border border-[#e2e8f0] rounded-md focus:outline-none focus:border-[#4f46e5] resize-none"
                      />
                      <div className="flex gap-1.5 justify-end">
                        <button onClick={() => setSendBackOpen(false)} className="h-7 px-2.5 rounded-md border border-[#e2e8f0] text-xs text-slate-600 cursor-pointer">Cancel</button>
                        <button
                          disabled={!sendBackNote.trim() || statusBusy}
                          onClick={() => void runStatusAction('send_back', sendBackNote.trim())}
                          className="h-7 px-2.5 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 cursor-pointer"
                        >
                          Send back
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-[#e2e8f0]">
                  <span className="text-sm text-slate-500">Link Status</span>
                  {linkToItemMaster ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <i className="ri-link-m" /> Linked
                    </span>
                  ) : (
                    <button
                      onClick={() => setShowLinkConfirm(true)}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 cursor-pointer"
                    >
                      Link to Item Master
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BOM Component Rules Banner */}
        <div className="bg-[#eff6ff] border border-[#bfdbfe] rounded-xl p-4 mb-3.5">
          <p className="text-xs sm:text-[13px] text-[#2563eb] leading-relaxed">
            <strong className="font-bold text-[#1d4ed8]">BOM Component Rules: </strong>
            Only Raw Material, Semi-Finished, Consumable and Packing items can be added as components. Finished Goods cannot be used as components. Semi-Finished items can have their own sub-components — click the + button to add them. The same raw material can appear at multiple levels if used both directly and inside a sub-assembly.
          </p>
        </div>

        {/* BOM Level Rules Toggle & Expandable Card */}
        <div className="mb-5">
          <button
            type="button"
            onClick={() => setShowLevelRules((prev) => !prev)}
            className="inline-flex items-center gap-1.5 text-xs sm:text-[13px] font-medium text-slate-600 hover:text-indigo-600 transition-colors cursor-pointer mb-2.5 select-none"
          >
            <i className="ri-information-line text-sm text-[#6366f1]" />
            <span>BOM Level Rules</span>
            <i
              className={`ri-arrow-down-s-line text-xs text-slate-400 transition-transform duration-200 ${
                showLevelRules ? '' : '-rotate-90'
              }`}
            />
          </button>

          {showLevelRules && (
            <div className="bg-[#f8fafc] border border-slate-200/80 rounded-xl p-4 sm:p-5">
              <div className="space-y-4">
                {/* Level 1 */}
                <div>
                  <h4 className="text-xs sm:text-[13px] font-bold text-slate-800 mb-2">
                    Level 1 (direct components of finished product):
                  </h4>
                  <ul className="space-y-1.5 text-xs sm:text-[13px] text-slate-600">
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-600 font-bold text-xs">✓</span>
                      <span>Raw Material</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-600 font-bold text-xs">✓</span>
                      <span>Semi-Finished (can have sub-components)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-600 font-bold text-xs">✓</span>
                      <span>Consumable / Packing Material</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-rose-500 font-bold text-xs">✕</span>
                      <span>Finished Good — NOT ALLOWED</span>
                    </li>
                  </ul>
                </div>

                {/* Level 2 */}
                <div>
                  <h4 className="text-xs sm:text-[13px] font-bold text-slate-800 mb-2">
                    Level 2 (sub-components of a Semi-Finished item):
                  </h4>
                  <ul className="space-y-1.5 text-xs sm:text-[13px] text-slate-600">
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-600 font-bold text-xs">✓</span>
                      <span>Raw Material</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-600 font-bold text-xs">✓</span>
                      <span>Consumable</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-rose-500 font-bold text-xs">✕</span>
                      <span>Finished Good — NOT ALLOWED</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-rose-500 font-bold text-xs">✕</span>
                      <span>Another Semi-Finished — avoid circular reference</span>
                    </li>
                  </ul>
                </div>

                {/* Footer note */}
                <div className="pt-3 border-t border-slate-200/70">
                  <p className="text-xs font-bold text-emerald-600 mb-0.5">
                    Same raw material at multiple levels = ALLOWED
                  </p>
                  <p className="text-xs text-slate-500">
                    (e.g. Steel Rod in pump directly AND inside gear sub-assembly)
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Components Table */}
        {/* Read-only while the BOM is active or obsolete */}
        <fieldset disabled={isLocked} className="min-w-0 border-0 p-0 m-0">
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden mb-5">
          <div className="flex items-center justify-between p-4 border-b border-[#e2e8f0]">
            <h3 className="text-sm font-semibold text-[#1e293b]">Components</h3>
            <div className="flex gap-2">
              <button
                onClick={handleCostRollup}
                className="h-8 px-3 rounded-lg border border-amber-200 text-xs font-medium text-amber-700 hover:bg-amber-50 cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-calculator-line" /> Recalculate Cost
              </button>
              <button
                onClick={() =>
                  openAddModal(
                    items.find((i) => i.parentId === null)?.id || null,
                  )
                }
                disabled={!selectedProduct}
                className="h-8 px-3 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-[#4338ca] disabled:opacity-40 cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-add-line" /> Add Component
              </button>
            </div>
          </div>

          {items.length <= 1 ? (
            <div className="text-center py-12 text-slate-400">
              <i className="ri-list-check-2 text-3xl mb-2 block" />
              <p className="text-sm">No components yet</p>
              <p className="text-xs mt-1">Add components to create this BOM</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-80">
                      Item
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0]">
                      Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0]">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-24 whitespace-nowrap">
                      Qty{baseQtyNum !== 1 ? ` / ${baseQtyNum}` : ''}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-24">
                      Unit
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-20">
                      Scrap%
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-24">
                      Eff.Qty
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-28">
                      Cost/Unit
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-28">
                      Total Cost
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 border-b border-[#e2e8f0] w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item) => {
                    const effQty = item.qtyPerUnit * (1 + item.scrapPct / 100);
                    const effStockQty = effQty * (item.toStock ?? 1);
                    const inOtherUnit = !!item.unitId && item.stockUnit && item.stockUnit !== item.unit;
                    const unitOptions = item.level > 0 ? unitOptionsFor(item) : [];
                    const childrenCount = items.filter(
                      (i) => i.parentId === item.id,
                    ).length;
                    const isExpanded = expandedRows.has(item.id);
                    const rolledUpCost = rolledUpCosts?.[item.id];
                    const totalCost =
                      rolledUpCost !== undefined && childrenCount > 0
                        ? rolledUpCost
                        : effStockQty * item.standardCost;

                    return (
                      <tr
                        key={item.id}
                        className="border-b border-slate-50 hover:bg-slate-50/50"
                      >
                        <td className="px-4 py-2.5">
                          <div
                            className="flex items-center gap-1"
                            style={{ paddingLeft: item.level * 16 }}
                          >
                            {childrenCount > 0 && (
                              <button
                                onClick={() => toggleExpand(item.id)}
                                className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-100 cursor-pointer"
                              >
                                <i
                                  className={`ri-arrow-down-s-line text-slate-400 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                                />
                              </button>
                            )}
                            {childrenCount === 0 && <span className="w-5" />}
                            <span
                              className={`text-sm ${item.level === 0 ? 'font-semibold text-[#1e293b]' : 'text-[#1e293b]'}`}
                            >
                              {item.itemName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-500">
                          {item.itemCode}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${typeBadgeClass(item.itemType)}`}
                          >
                            {typeLabel(item.itemType)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.qtyPerUnit || ''}
                            onChange={(e) =>
                              updateItemField(item.id, {
                                qtyPerUnit: parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0,
                              })
                            }
                            className="w-20 h-8 px-2 text-sm bg-white border border-[#e2e8f0] rounded-md focus:outline-none focus:border-[#4f46e5] text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-600">
                          {unitOptions.length > 1 ? (
                            <select
                              value={unitOptions.find((u) => u.shortName === item.unit)?.id ?? ''}
                              onChange={(e) => changeItemUnit(item, e.target.value)}
                              title={`Stock unit: ${item.stockUnit || item.unit}`}
                              className="h-8 px-1.5 text-sm bg-white border border-[#e2e8f0] rounded-md focus:outline-none focus:border-[#4f46e5] cursor-pointer"
                            >
                              {unitOptions.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.shortName}
                                </option>
                              ))}
                            </select>
                          ) : (
                            item.unit
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.scrapPct || ''}
                            onChange={(e) =>
                              updateItemField(item.id, {
                                scrapPct: parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0,
                              })
                            }
                            className="w-16 h-8 px-2 text-sm bg-white border border-[#e2e8f0] rounded-md focus:outline-none focus:border-[#4f46e5] text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right text-sm text-slate-600 whitespace-nowrap">
                          {inOtherUnit ? (
                            <>
                              {fmtQty(effQty)} {item.unit}
                              <span className="block text-[11px] text-slate-400">
                                ({fmtQty(effStockQty)} {item.stockUnit})
                              </span>
                            </>
                          ) : (
                            effQty.toFixed(3)
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right text-sm text-slate-600 whitespace-nowrap">
                          {formatINR(item.standardCost)}
                          {inOtherUnit && (
                            <span className="block text-[11px] text-slate-400">per {item.stockUnit}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right text-sm font-medium text-[#1e293b]">
                          {formatINR(totalCost)}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-center gap-1">
                            {item.itemType === 'SEMI_FINISHED' && (
                              <button
                                onClick={() => openAddModal(item.id)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-[#4f46e5] cursor-pointer"
                              >
                                <i className="ri-add-line text-sm" />
                              </button>
                            )}
                            {item.level > 0 && (
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </fieldset>

        {/* COST SUMMARY - SIRF 3 LINES */}
        {displayItems.length > 0 && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-[#e2e8f0] space-y-2">
            {costSummary.packaging > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Packaging:</span>
                <span className="text-sm font-medium text-[#4f46e5]">
                  {formatINR(costSummary.packaging)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center pt-1">
              <span className="text-sm font-semibold text-[#1e293b]">
                Total Material Cost{baseQtyNum !== 1 ? ` (for ${baseQtyNum} ${selectedProduct?.unit_name || 'units'})` : ''}:
              </span>
              <span className="text-sm font-bold text-[#1e293b]">
                {formatINR(costSummary.total)}
              </span>
            </div>
            {baseQtyNum !== 1 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Per {selectedProduct?.unit_name || 'unit'}:</span>
                <span className="text-sm font-medium text-[#1e293b]">{formatINR(costSummary.total / baseQtyNum)}</span>
              </div>
            )}
            {costSummary.scrapCost > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-amber-600">Scrap Cost:</span>
                <span className="text-sm font-medium text-amber-600">
                  {formatINR(costSummary.scrapCost)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Click outside to close product search */}
        {showProductSearch && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowProductSearch(false)}
          />
        )}

        {/* Modals */}
        <AddComponentModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setAddModalParentId(null);
          }}
          onSelect={handleAddComponent}
          excludeItemIds={selectedProductId ? [selectedProductId] : []}
          items={availableComponents}
        />

        {showExplodeModal && (
          <BOMExplosionModal
            isOpen={showExplodeModal}
            onClose={() => setShowExplodeModal(false)}
            bom={currentBOMObject}
          />
        )}

        {showPrintModal && (
          <BOMPrintModal
            isOpen={showPrintModal}
            onClose={() => setShowPrintModal(false)}
            bom={currentBOMObject}
            customItems={displayItems.map((it) => ({
              ...it,
              standardCost: it.standardCost * (it.toStock ?? 1),
            }))}
          />
        )}

        {/* Link to Item Master Confirmation Dialog */}
        <ConfirmDialog
          open={showLinkConfirm}
          title="Link to Item Master"
          message={`Link this BOM to ${selectedProduct?.name || 'product'} and update standard cost?`}
          variant="warning"
          confirmLabel="Link"
          cancelLabel="Cancel"
          onConfirm={async () => {
            try {
              if (isEditing && editId) {
                await bomAPI.linkToItemMaster(editId);
                setLinkToItemMaster(true);
                toast.success(`BOM linked to ${selectedProduct?.name}`);
              } else {
                setLinkToItemMaster(true);
                toast.info(`Will be linked when BOM is saved`);
              }
            } catch (error) {
              toast.error('Failed to link BOM');
            }
            setShowLinkConfirm(false);
          }}
          onCancel={() => setShowLinkConfirm(false)}
        />

        {/* Cost Confirm Dialog */}
        <ConfirmDialog
          open={showCostConfirm}
          title="Cost Rollup"
          message={`Total calculated cost: ${formatINR(rolledUpTotal)}`}
          variant="default"
          confirmLabel="OK"
          cancelLabel="Cancel"
          onConfirm={() => setShowCostConfirm(false)}
          onCancel={() => setShowCostConfirm(false)}
        />

        {/* Duplicate Confirm Dialog */}
        <ConfirmDialog
          open={showDuplicateConfirm}
          title="Duplicate BOM"
          message="Create a new draft version from this BOM?"
          variant="warning"
          confirmLabel="Duplicate"
          cancelLabel="Cancel"
          onConfirm={async () => {
            try {
              if (editId) {
                const response = await bomAPI.duplicate(editId);
                if (response.success) {
                  toast.success('BOM duplicated successfully');
                  navigate(`/manufacturing/bom/${response.data.id}/edit`);
                }
              }
            } catch (error) {
              toast.error('Failed to duplicate BOM');
            }
            setShowDuplicateConfirm(false);
          }}
          onCancel={() => setShowDuplicateConfirm(false)}
        />
      </div>
    </AppLayout>
  );
}
